import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { api, completeLogin, describeAuthRefusal } from '@/lib/api';
import { postLoginPath } from '@/lib/mcp-consent';
import { AuthCard } from './AuthCard';

interface RecoveryResponse {
  accessToken: string;
  mustRegenerateRecoveryCodes: boolean;
}

export function MfaRecoveryPage() {
  const navigate = useNavigate();
  const [recoveryCode, setRecoveryCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { data } = await api.post<RecoveryResponse>(
        '/platform/auth/mfa/recovery',
        { recoveryCode },
      );
      // Só o access, em memória: o refresh chega no cookie platform_refresh (HttpOnly).
      completeLogin(data);
      // A troca dos códigos vem antes de tudo: o SPA é o único lugar que a força, e o backend
      // sempre a pede no login por recovery code. Uma autorização de agente pendente não desvia
      // daqui (um link qualquer para /mcp/autorizar basta para gravar a chave); a query fica no
      // sessionStorage, com a validade dela, e a sessão de recovery code não aprovaria mesmo
      // (a API responde reauth_required).
      if (data.mustRegenerateRecoveryCodes) {
        navigate('/profile?regenerate=1');
        return;
      }
      // Sem troca pedida, o destino é o de sempre: o caminho fixo do consentimento com chave
      // pendente, /tenants sem ela.
      navigate(postLoginPath());
    } catch (err) {
      setError(describeAuthRefusal(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      title="Recovery code"
      subtitle="Use um dos códigos que você salvou no setup do MFA."
    >
      <form onSubmit={onSubmit}>
        {error && <div className="error-banner">{error}</div>}
        <div className="info-banner">
          Cada recovery code só funciona uma vez. Após o login, gere um novo
          set de códigos imediatamente.
        </div>

        <div className="field">
          <label htmlFor="recoveryCode">Código (xxxx-xxxx-xxxx)</label>
          <input
            id="recoveryCode"
            type="text"
            value={recoveryCode}
            onChange={(e) => setRecoveryCode(e.target.value)}
            placeholder="ABCD-EFGH-JKLM"
            required
            autoFocus
          />
        </div>

        <button type="submit" className="btn" style={{ width: '100%' }} disabled={submitting}>
          {submitting ? 'Validando…' : 'Entrar'}
        </button>
      </form>
    </AuthCard>
  );
}
